import { useApiClient } from '@klareospace/pharos-lib-front-utils';
import type { Order } from '@klareospace/pharos-lib-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createMutationCallbacks } from '@klareospace/pharos-lib-web-utils';

import { createOrdersApi, orderKeys } from '../api';
import type { UpdateOrderRequest } from '../api';

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Wraps PATCH /orders/:id. Deliberately silent on success/error (no toast, no errorKey) —
 * an impacting update cancels + recreates the active task, so the order detail, the orders
 * search/list, and the order's tasks are all invalidated. Callers own success/error feedback
 * (toast for inline edits, ErrorBanner for pop-ins) via order-update-error.ts.
 * Every tracked field edit writes a history feed entry, and an impacting update can transition
 * the order status (task cancel + recreate, re-validation), which feeds the lifecycle timeline —
 * so history and status history are invalidated too.
 */
export function useUpdateOrder(orderId: string): UseMutationResult<Order, Error, UpdateOrderRequest> {
  const client = useApiClient('deliveries');
  const api = createOrdersApi(client);
  const queryClient = useQueryClient();

  return useMutation<Order, Error, UpdateOrderRequest>({
    mutationFn: (body: UpdateOrderRequest) => api.updateOrder(orderId, body),
    ...createMutationCallbacks<Order, UpdateOrderRequest>({
      invalidateKeys: [
        orderKeys.detail(orderId),
        orderKeys.lists(),
        orderKeys.tasks(orderId),
        orderKeys.history(orderId),
        orderKeys.statusHistory(orderId),
      ],
      queryClient,
    }),
  });
}
